import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SearchLifestyleIntervention from '../../components/SearchLifestyleIntervention/SearchLifestyleIntervention';
import { ReactComponent as SVGLogo } from '../../images/icons/logo.svg';
import './SearchPage.scss';
import { useTranslation } from 'react-i18next';
import DiseaseSymptomInput from '../../components/DiseaseSymptomInput/DiseaseSymptomInput';
import { useService } from 'react-service-locator';
import { SearchService } from '../../services/search.service';
import { max } from 'lodash';
import { LifestyleInterventionService } from '../../services/lifestyle-intervention.service';
import { DiseaseService } from '../../services/disease.service';
import Select, { StylesConfig } from 'react-select';
import { useHistory } from 'react-router-dom';

// SearchPage.tsx
const customSelectStyles: StylesConfig<{ label: string; value: string; disabled: boolean }> = {
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        height: 48,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: state.isDisabled ? '#D7DCE8' : state.isFocused ? '#3A58A1' : '#B9C3DA',
        backgroundColor: state.isDisabled ? '#E9EDF6' : '#FFFFFF',
        boxShadow: 'none',
        ':hover': { borderColor: state.isDisabled ? '#D7DCE8' : '#3A58A1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 12px' }),
    placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#A7B1C7' : '#8E99B7',
        fontWeight: 500,
    }),
    singleValue: (base) => ({ ...base, color: '#0B2566', fontWeight: 600 }),
    input: (base) => ({ ...base, color: '#0B2566', fontWeight: 600, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        paddingRight: 12,
        paddingLeft: 4,
        color: state.isDisabled ? '#A7B1C7' : '#3A58A1',
    }),
    menu: (base) => ({ ...base, zIndex: 5, borderRadius: 8, overflow: 'hidden' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#EDF1FA' : state.isFocused ? '#F4F6FB' : 'transparent',
        color: state.isDisabled ? '#8E99B7' : '#0B2566',
        fontWeight: state.isDisabled ? 500 : 600,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
};

const SearchPage: React.FC = () => {
    const searchService = useService(SearchService);
    const diseaseService = useService(DiseaseService);
    const lifestyleInterventionService = useService(LifestyleInterventionService);
    const history = useHistory();
    const { t } = useTranslation();
    useEffect(() => {
        const searchPageCard = document.getElementById('searchpage')?.getElementsByTagName('div')[0];
        if (!searchPageCard || !searchPageCard.classList.contains('card')) {
            return;
        }
        const lifestyleInterventionButtons = Array.from(document.getElementsByClassName('LifestyleInterventionButton'));
        const highestContentHeight = max(
            lifestyleInterventionButtons.map((c) => {
                const el = Array.from(c.getElementsByTagName('div')).find((c) => c.classList.contains('content'));
                if (!el) {
                    return;
                }
                return el.offsetHeight;
            }),
        );
        const marginBottom = max([0, (highestContentHeight ?? 0) - 155]);
        searchPageCard.style.marginBottom = `${marginBottom}px`;
    }, [lifestyleInterventionService.state.lifestyleInterventions]);
    useEffect(() => {
        lifestyleInterventionService.fetchLifestyleInterventions();
    }, []);

    const toMutable = <T,>(xs: ReadonlyArray<T> | T[] | null | undefined): T[] => (xs ? Array.from(xs) : []);

    // Hardcoded: Subcategory -> allowed Symptom values (exact display strings)
    const CARDIO_MAP: Record<string, string[]> = {
        'heart disease': [
            'Angina pectoris',
            'Arteriosclerosis',
            'Atrium fibrillation',
            'Cardiomyopathies',
            'Coronary arteries',
            'Coronary microcirculation',
            'Endocarditis',
            'Heart attack',
            'Heart failure',
            'Heart rhythm disorder',
            'Heart valve disease',
            'Myocarditis',
            'Pericarditis',
            'Pulmonate hypertension',
        ],
        'vascular disease': ['Aneurysm', 'Narrowing carotid artery', 'SCAD', 'Thrombosis'],
        'cerebral disease': [
            'Cerebral infarction',
            'CVA',
            'Cerebral Vascular Accident',
            'Stroke',
            'TIA',
            'Transient Ischaemic Attack',
        ],
    };

    // Current selections (mutable copies)
    const selectedSubcats = toMutable(searchService.state.subcategories);
    const selectedSyms = toMutable(searchService.state.symptoms);

    const allowedSymptomSet = React.useMemo(() => {
        if (!selectedSubcats.length) return null; // no subcat selected → show all

        // take union across the selected CV subcats we know about
        const out = new Set<string>();
        for (const sc of selectedSubcats) {
            const list = CARDIO_MAP[sc.toLowerCase()];
            if (Array.isArray(list)) list.forEach((v) => out.add(v));
        }

        // if none of the selected subcats are in our CV map, don't filter
        return out.size ? out : null;
    }, [selectedSubcats]);

    // Build base symptom options from your service (strings)
    const allSymptomOptionsRaw = React.useMemo(
        () =>
            (diseaseService.state.symptoms ?? []).map((c: string) => ({
                value: c,
                label: c,
                disabled: false,
            })),
        [diseaseService.state.symptoms],
    );

    const filteredSymptomOptions = React.useMemo(() => {
        if (!allowedSymptomSet) return allSymptomOptionsRaw; // show all when no filter
        return allSymptomOptionsRaw.filter((opt) => allowedSymptomSet.has(opt.value));
    }, [allowedSymptomSet, allSymptomOptionsRaw]);

    React.useEffect(() => {
        if (!allowedSymptomSet) return;
        const pruned = selectedSyms.filter((s) => allowedSymptomSet.has(s));
        if (pruned.length !== selectedSyms.length) {
            searchService.setSymptoms([...pruned]); // pass mutable
        }
    }, [allowedSymptomSet]);

    useEffect(() => {
        searchService.clearResult();
    }, []);

    const onSearch = () => {
        if (searchService.state.disease == null) {
            return;
        }
        searchService.search();
        history.push('/results');
    };

    return (
        <div id="searchpage" className="search">
            <div className="search-card">
                <div className="title">
                    <SVGLogo height={50} width="auto" />
                </div>
                <div className="disease-symptom-wrapper">
                    <h3>{t('pages.search-page.search-terms')}</h3>
                    <DiseaseSymptomInput />
                </div>
                <div className="lifestyle-intervention-wrapper" id="lifestyleInterventionWrapper">
                    <p className="body body-normal">{t('pages.search-page.lifestyle-intervention')}</p>
                    <SearchLifestyleIntervention />
                </div>
                <div className="disease-symptom-row">
                    <div className="field">
                        <p
                            className={`label body body-normal${
                                diseaseService.state.symptoms && diseaseService.state.symptoms.length !== 0
                                    ? ''
                                    : ' disabled'
                            }`}
                        >
                            {t('pages.search-page.subcategory')}
                        </p>
                        <div className="select-wrap">
                            <Select
                                className="select body body-normal"
                                classNamePrefix="rs"
                                value={searchService.state.subcategories.map((s) => ({
                                    label: s,
                                    value: s,
                                    disabled: false,
                                }))}
                                isMulti
                                options={
                                    diseaseService.state.subcategories?.map((c) => ({
                                        value: c,
                                        label: c,
                                        disabled: false,
                                    })) ?? []
                                }
                                onChange={(v) => searchService.setSubcategories(v?.map((s) => s.value))}
                                isDisabled={
                                    !diseaseService.state.subcategories ||
                                    diseaseService.state.subcategories.length === 0
                                }
                                placeholder={t('components.search-subcategory.select')}
                                styles={customSelectStyles}
                            />
                        </div>
                    </div>

                    <div className="field">
                        <p
                            className={`label body body-normal${
                                diseaseService.state.symptoms && diseaseService.state.symptoms.length !== 0
                                    ? ''
                                    : ' disabled'
                            }`}
                        >
                            {t('pages.search-page.symptom')}
                        </p>
                        <div className="select-wrap">
                            <Select
                                className="select body body-normal"
                                classNamePrefix="rs"
                                value={toMutable(searchService.state.symptoms).map((s: string) => ({
                                    label: s,
                                    value: s,
                                    disabled: false,
                                }))}
                                isMulti
                                options={filteredSymptomOptions}
                                onChange={(v) => searchService.setSymptoms(Array.from(v ?? [], (s: any) => s.value))}
                                isDisabled={
                                    !diseaseService.state.symptoms ||
                                    diseaseService.state.symptoms.length === 0 ||
                                    filteredSymptomOptions.length === 0
                                }
                                placeholder={t('components.search-symptom.select')}
                                styles={customSelectStyles}
                                // Safety: if options get widened elsewhere, keep disallowed ones unselectable while CV filter is active
                                isOptionDisabled={(opt) => !!allowedSymptomSet && !allowedSymptomSet.has(opt.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="btn-wrapper">
                    <button
                        onClick={onSearch}
                        className={`search-button body body-bold-big btn ${
                            searchService.state.disease == null ? 'btn-enabled' : ''
                        }`}
                        disabled={searchService.state.disease == null}
                    >
                        {t('search')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchPage;
